import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { DropMenu } from "@art/shared-ui";
import {
  faBell,
  faBoxOpen,
  faKey,
  faSatelliteDish,
  faCircleCheck,
  faCheckDouble,
  faUserShield,
  faTriangleExclamation,
  faCommentDots,
  faCircleInfo,
  faArrowsRotate,
  faCreditCard,
  faCircleExclamation,
  faCalendarXmark,
  faClock,
  faUserPlus,
  faShieldHalved,
  faFlagCheckered,
  faListCheck,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";
import FaIcon from "../utils/fa_icon";
import { RootState } from "../../store";
import { InboxItem } from "../../store/slice/inboxSlice";
import { useInbox } from "./useInbox";
import NotificationActionsIFrame from "./notification-actions-iframe";
import styles from "./notification-bell.module.scss";

type Tab = "all" | "unread";



function visualFor(type: string): { icon: any; tone: string } {
  const t = (type || "").toLowerCase();

  // Governance / human-in-the-loop
  if (t === "governance.approval") return { icon: faUserShield, tone: "amber" };
  if (t === "governance.escalate") return { icon: faTriangleExclamation, tone: "red" };
  if (t === "governance.feedback") return { icon: faCommentDots, tone: "violet" };
  if (t === "governance.inform") return { icon: faCircleInfo, tone: "blue" };
  if (t === "governance.update") return { icon: faArrowsRotate, tone: "blue" };
  if (t.startsWith("governance")) return { icon: faUserShield, tone: "amber" };

  // Payments and saved cards
  if (t === "payment.failed") return { icon: faCircleExclamation, tone: "red" };
  if (t === "payment.captured") return { icon: faCircleCheck, tone: "green" };
  if (t === "payment.authorized") return { icon: faCreditCard, tone: "blue" };
  if (t === "token.rejected") return { icon: faCreditCard, tone: "red" };
  if (t === "token.confirmed") return { icon: faCreditCard, tone: "green" };
  if (t.startsWith("payment") || t.startsWith("token")) return { icon: faCreditCard, tone: "blue" };

  // Subscription lifecycle
  if (t === "subscription.expired") return { icon: faCalendarXmark, tone: "red" };
  if (t.startsWith("subscription")) return { icon: faClock, tone: "amber" };

  // Identity and security
  if (t.startsWith("invite")) return { icon: faUserPlus, tone: "violet" };
  if (t.startsWith("security")) return { icon: faShieldHalved, tone: "amber" };
  if (t.startsWith("password")) return { icon: faKey, tone: "violet" };

  // Orchestration
  if (t === "mission") return { icon: faFlagCheckered, tone: "blue" };
  if (t === "task") return { icon: faListCheck, tone: "blue" };
  if (t === "resume") return { icon: faPlay, tone: "green" };

  // Older types, kept so historical notifications still render correctly
  if (t.startsWith("order")) return { icon: faBoxOpen, tone: "amber" };
  if (t.startsWith("client_credential")) return { icon: faKey, tone: "violet" };
  if (t.startsWith("channel")) return { icon: faSatelliteDish, tone: "blue" };

  return { icon: faBell, tone: "slate" };
}

function scopeLabel(item: InboxItem): string {
  const d = (item.data || {}) as Record<string, any>;
  return (d.project_name as string) || (d.environment as string) || "";
}

function relativeTime(iso: string): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const { items, unreadCount } = useSelector((s: RootState) => s.inbox);
  const { markOne, markAll, refresh } = useInbox();
  const [tab, setTab] = useState<Tab>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const projects = useMemo(() => {
    const set = new Set<string>();
    items.forEach((i) => {
      const p = scopeLabel(i);
      if (p) set.add(p);
    });
    return Array.from(set).sort();
  }, [items]);


  const visible = useMemo(
    () =>
      items.filter((i) => {
        if (tab === "unread" && i.status !== "unread") return false;
        if (projectFilter !== "all" && scopeLabel(i) !== projectFilter) return false;
        return true;
      }),
    [tab, projectFilter, items]
  );

  const handleItemClick = (item: InboxItem) => {
    if (item.status === "unread") markOne(item.id);
  };

  const handleResolved = useCallback(
    (item: InboxItem) => {
      if (item.status === "unread") markOne(item.id);
      refresh();
    },
    [markOne, refresh]
  );

  return (
    <DropMenu
      menuWidth="min(440px, calc(100vw - 32px))"
      menuPadding="0px"
      triggerComponent={
        <span className={styles.bell_wrapper} data-qa="header-notification-bell">
          <FaIcon icon={faBell} className={styles.bell_icon} />
          {unreadCount > 0 && (
            <span className={styles.badge} data-qa="header-notification-badge">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </span>
      }
      children={() => (
        <div className={styles.panel} data-notification-panel>
          {/* Pinned top (header + tabs) — stays put while the list scrolls */}
          <div className={styles.panel_head}>
          {/* Header */}
          <div className={styles.panel_header}>
            <div className={styles.panel_heading}>
              <span className={styles.panel_title}>Notifications</span>
              {unreadCount > 0 && (
                <span className={styles.unread_chip}>{unreadCount} new</span>
              )}
            </div>
            <button
              type="button"
              className={styles.mark_all}
              onClick={markAll}
              disabled={unreadCount === 0}
              data-qa="header-notification-mark-all"
            >
              <FaIcon icon={faCheckDouble} className={styles.mark_all_icon} />
              Mark all read
            </button>
          </div>

          {/* Tabs */}
          <div className={styles.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              className={`${styles.tab} ${tab === "all" ? styles.tab_active : ""}`}
              onClick={() => setTab("all")}
            >
              All
            </button>
            <button
              type="button"
              role="tab"
              className={`${styles.tab} ${tab === "unread" ? styles.tab_active : ""}`}
              onClick={() => setTab("unread")}
            >
              Unread
              {unreadCount > 0 && <span className={styles.tab_count}>{unreadCount}</span>}
            </button>
            {projects.length > 1 && (
              <select
                className={styles.project_filter}
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                data-qa="header-notification-project-filter"
              >
                <option value="all">All projects</option>
                {projects.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            )}
          </div>
          </div>

          {/* Body */}
          {visible.length === 0 ? (
            <div className={styles.empty}>
              <FaIcon icon={faCircleCheck} className={styles.empty_icon} />
              <span className={styles.empty_title}>You&apos;re all caught up</span>
              <span className={styles.empty_sub}>
                {tab === "unread"
                  ? "No unread notifications."
                  : "New notifications will show up here."}
              </span>
            </div>
          ) : (
            <div className={styles.list} data-qa="header-notification-list">
              {visible.map((item) => {
                const v = visualFor(item.type);
                const unread = item.status === "unread";
                return (
                  <div
                    key={item.id}
                    className={`${styles.item} ${unread ? styles.item_unread : ""}`}
                    onClick={() => handleItemClick(item)}
                    data-qa={`header-notification-item-${item.id}`}
                  >
                    <span className={`${styles.item_icon} ${styles[`tone_${v.tone}`]}`}>
                      <FaIcon icon={v.icon} />
                    </span>
                    <div className={styles.item_main}>
                      <div className={styles.item_row}>
                        <span className={styles.item_title}>
                          {item.title || "Notification"}
                        </span>
                        <span className={styles.item_time}>
                          {relativeTime(item.created_at)}
                        </span>
                      </div>
                      {item.body && <span className={styles.item_body}>{item.body}</span>}
                      {scopeLabel(item) && (
                        <span className={styles.item_project}>{scopeLabel(item)}</span>
                      )}
                      <NotificationActionsIFrame item={item} onResolved={handleResolved} />
                    </div>
                    {unread && <span className={styles.unread_dot} aria-label="unread" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    />
  );
}
